export const CHART_HEIGHT = 1000;
export const CHART_WIDTH = 1000;
const HEADROOM = 0.94;

const NICE_STEPS = [1, 2, 5];

export const niceStep = (raw) => {
  const target = Math.max(1, Math.ceil(raw));
  const magnitude = 10 ** Math.floor(Math.log10(target));
  const step = NICE_STEPS.map((base) => base * magnitude).find((value) => value >= target);
  return step || 10 * magnitude;
};

export const yTicks = (values = [], lines = 3) => {
  const max = Math.max(0, ...values.map((value) => Number(value) || 0));
  const step = niceStep(max / (lines - 1));
  return Array.from({ length: lines }, (_, index) => step * index);
};

export const xLabelIndexes = (count, labels = 5) => {
  if (count <= 0) return [];
  if (count <= labels) return Array.from({ length: count }, (_, index) => index);
  const last = count - 1;
  const picks = Array.from({ length: labels }, (_, index) => Math.round((index * last) / (labels - 1)));
  return [...new Set(picks)];
};

export const pointX = (index, count) => (count <= 1 ? CHART_WIDTH / 2 : (index / (count - 1)) * CHART_WIDTH);

export const pointY = (value, top) =>
  top > 0 ? CHART_HEIGHT - ((Number(value) || 0) / top) * CHART_HEIGHT * HEADROOM : CHART_HEIGHT;

export const indexAt = (ratio, count) => {
  if (count <= 1) return 0;
  const clamped = Math.min(1, Math.max(0, ratio));
  return Math.round(clamped * (count - 1));
};

export const formatDay = (value) => {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).replace(" ", "\u00a0");
};

export const seriesFor = (series, key) => {
  const days = series?.days;
  const current = series?.current?.[key];
  if (!Array.isArray(days) || !Array.isArray(current) || !days.length || current.length !== days.length) return null;
  const previous = series?.previous?.[key];
  return {
    days,
    current: current.map((value) => Number(value) || 0),
    previous: Array.isArray(previous) && previous.length === days.length ? previous.map((value) => Number(value) || 0) : null,
  };
};

export const cumulative = (values = []) => {
  let total = 0;
  return values.map((value) => {
    total += Number(value) || 0;
    return total;
  });
};

export const chartPoints = (values = [], top) =>
  values.map((value, index) => [pointX(index, values.length), pointY(value, top)]);

const tangents = (points) => {
  const count = points.length;
  const slopes = [];
  for (let index = 0; index < count - 1; index += 1) {
    const [x0, y0] = points[index];
    const [x1, y1] = points[index + 1];
    slopes.push(x1 === x0 ? 0 : (y1 - y0) / (x1 - x0));
  }
  const result = points.map((_, index) => {
    if (index === 0) return slopes[0];
    if (index === count - 1) return slopes[count - 2];
    const before = slopes[index - 1];
    const after = slopes[index];
    return before * after <= 0 ? 0 : (before + after) / 2;
  });
  slopes.forEach((slope, index) => {
    if (slope === 0) {
      result[index] = 0;
      result[index + 1] = 0;
      return;
    }
    const a = result[index] / slope;
    const b = result[index + 1] / slope;
    const size = a * a + b * b;
    if (size > 9) {
      const scale = 3 / Math.sqrt(size);
      result[index] = scale * a * slope;
      result[index + 1] = scale * b * slope;
    }
  });
  return result;
};

const fixed = (value) => Number(value.toFixed(1));

export const smoothPath = (points = []) => {
  if (!points.length) return "";
  if (points.length === 1) return `M${fixed(points[0][0])} ${fixed(points[0][1])}`;
  const slope = tangents(points);
  let path = `M${fixed(points[0][0])} ${fixed(points[0][1])}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const [x0, y0] = points[index];
    const [x1, y1] = points[index + 1];
    const third = (x1 - x0) / 3;
    path += ` C${fixed(x0 + third)} ${fixed(y0 + slope[index] * third)} ${fixed(x1 - third)} ${fixed(
      y1 - slope[index + 1] * third,
    )} ${fixed(x1)} ${fixed(y1)}`;
  }
  return path;
};

export const smoothAreaPath = (points = [], base = CHART_HEIGHT) => {
  if (!points.length) return "";
  const first = fixed(points[0][0]);
  const last = fixed(points[points.length - 1][0]);
  return `${smoothPath(points)} L${last} ${base} L${first} ${base} Z`;
};

export const pointerIndex = ({ clientX, left, width }, count) => {
  if (!width) return null;
  return indexAt((clientX - left) / width, count);
};

export const hourLabel = (start, index) => {
  const base = start ? new Date(start) : null;
  if (!base || Number.isNaN(base.getTime())) return "";
  const at = new Date(base.getTime() + index * 3600 * 1000);
  return at.toLocaleTimeString("en-US", { hour: "numeric" });
};

export const stepRate = (from, to) => {
  const start = Number(from) || 0;
  if (!start) return null;
  const rate = ((Number(to) || 0) / start) * 100;
  return `${rate >= 10 ? Math.round(rate) : Number(rate.toFixed(1))}%`;
};
